import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Rocket } from "lucide-react";

export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/cashfree/status/${orderId}`);
        setInfo(data);
        if (data.fulfilled || data.status === "PAID") {
          await refresh();
          if (!stop) setState("success");
          return;
        }
        if (["FAILED", "EXPIRED", "CANCELLED", "TERMINATED"].includes(data.status)) {
          if (!stop) setState("failed");
          return;
        }
      } catch (_) { /* keep polling */ }
      tries.current += 1;
      if (tries.current < 12 && !stop) setTimeout(poll, 2500);
      else if (!stop) setState("failed");
    };
    poll();
    return () => { stop = true; };
  }, [orderId]);

  const isBoost = info?.kind === "feature";

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-return-page">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 size={44} className="mx-auto brand-forest animate-spin mb-6" />
          <h1 className="font-display text-3xl brand-forest mb-2">Confirming your payment…</h1>
          <p className="text-gray-600">Hang tight — we're verifying this with the payment gateway. This only takes a moment.</p>
        </div>
      )}

      {state === "success" && (
        <div data-testid="payment-success">
          <div className="w-16 h-16 rounded-full bg-[#E9F9EF] flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={36} className="text-[#1FA463]" />
          </div>
          <h1 className="font-display text-3xl brand-forest mb-2">Payment successful 🎉</h1>
          <p className="text-gray-600 mb-8">
            {isBoost
              ? "Your listing is now boosted and pinned to the top of search results."
              : `Your ${info?.package_name || "package"} is active — your extra owner contacts are ready to use.`}
          </p>
          <div className="flex items-center justify-center gap-3">
            {isBoost ? (
              <Button asChild className="tlr-gold-btn rounded-full h-11 px-6">
                <Link to="/dashboard" data-testid="return-dashboard">
                  <Rocket size={16} className="mr-1" /> Go to my listings
                </Link>
              </Button>
            ) : (
              <Button asChild className="tlr-gold-btn rounded-full h-11 px-6">
                <Link to="/properties" data-testid="return-browse">Start revealing contacts</Link>
              </Button>
            )}
            <Button asChild variant="ghost" className="rounded-full h-11 px-5">
              <Link to="/dashboard">Dashboard</Link>
            </Button>
          </div>
        </div>
      )}

      {state === "failed" && (
        <div data-testid="payment-failed">
          <div className="w-16 h-16 rounded-full bg-[#FDECEC] flex items-center justify-center mx-auto mb-6">
            <XCircle size={36} className="text-[#D64545]" />
          </div>
          <h1 className="font-display text-3xl brand-forest mb-2">Payment not completed</h1>
          <p className="text-gray-600 mb-8">
            We didn't receive a confirmed payment for this order, so nothing was charged and no upgrade was applied. You can safely try again.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button asChild className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-11 px-6">
              <Link to={isBoost ? "/dashboard" : "/pricing"} data-testid="return-retry">Try again</Link>
            </Button>
            <Button asChild variant="ghost" className="rounded-full h-11 px-5">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
